var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { defaultPageSize } from "healthex-common/constants";
import { WebhookConfigurationStatus, WebhookDeliveryStatus, } from "healthex-common/webhook.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class AdminWebhookListViewModel {
    constructor(opts) {
        this.paginationInput = { page: 0, pageSize: defaultPageSize };
        this.total = 0;
        this.loadedWebhookConfigurations = NOT_LOADED;
        this.addWebhookDialogOpen = false;
        this.secretRevealState = null;
        this.deleteConfirmationDialogModel = new SimpleDialogModel();
        this.rotateConfirmationDialogModel = new SimpleDialogModel();
        this.bannerUIManager = opts.container.bannerUIManager;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
        this.routingUIManager = opts.container.routingUIManager;
        void this.loadWebhookConfigurations();
    }
    async loadWebhookConfigurations(skipLoadingState = false) {
        if (!skipLoadingState)
            this.loadedWebhookConfigurations = NOT_LOADED;
        if (!this.authManager.orgId)
            return;
        const start = this.paginationInput.page * this.paginationInput.pageSize;
        const end = start + this.paginationInput.pageSize;
        const result = await this.provisioningService.getWebhookConfigurations(this.authManager.orgId, start, end);
        this.loadedWebhookConfigurations = result.results;
        this.total = result.total;
    }
    get webhooks() {
        if (this.loadedWebhookConfigurations === NOT_LOADED)
            return NOT_LOADED;
        return this.loadedWebhookConfigurations.map((webhook) => {
            var _a, _b;
            return ({
                id: webhook.id,
                name: webhook.name,
                endpointUrl: webhook.webhookUrl,
                projectScope: {
                    orgWide: webhook.studyScope === null,
                    projects: (_b = (_a = webhook.studyScopeProjects) === null || _a === void 0 ? void 0 : _a.map((project) => project.title)) !== null && _b !== void 0 ? _b : [],
                },
                events: webhook.eventTypes,
                status: webhook.status,
                lastDeliveryLabel: webhook.lastDeliveryLogTimestamp
                    ? webhook.lastDeliveryLogTimestamp.toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    })
                    : "",
                recentHealth: webhook.recentStatuses.map((status) => status === WebhookDeliveryStatus.DELIVERED ? "OK" : status === WebhookDeliveryStatus.RETRIED ? "WARN" : "FAIL"),
            });
        });
    }
    updatePagination(paginationParams) {
        this.paginationInput = paginationParams;
        void this.loadWebhookConfigurations();
    }
    addWebhook() {
        this.addWebhookDialogOpen = true;
    }
    closeAddWebhook(updated) {
        this.addWebhookDialogOpen = false;
        if (updated) {
            void this.loadWebhookConfigurations(true);
        }
    }
    async onWebhookCreated(result, sendTestEvent) {
        let testEventSent = false;
        if (sendTestEvent) {
            testEventSent = await this.sendTestEvent(result);
        }
        this.secretRevealState = {
            kind: "created",
            webhookName: result.name,
            signingSecret: result.webhookSecret,
            testEventSent,
        };
        this.closeAddWebhook(true);
    }
    closeSecretReveal() {
        this.secretRevealState = null;
    }
    viewDetails(webhook) {
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_WEBHOOK_DETAIL, { webhookId: webhook.id });
    }
    viewDeliveryLog(webhook) {
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_WEBHOOK_DETAIL, {
            webhookId: webhook.id,
            tab: "deliveries",
        });
    }
    async rotateSecret(webhook) {
        if (!this.authManager.orgId)
            return;
        const confirmed = await this.rotateConfirmationDialogModel.showAndWait();
        if (!confirmed)
            return;
        const rotateResult = await this.provisioningService.rotateWebhookSecret(this.authManager.orgId, webhook.id);
        this.secretRevealState = {
            kind: "rotated",
            webhookName: webhook.name,
            signingSecret: rotateResult.webhookSecret,
            testEventSent: false,
        };
        void this.loadWebhookConfigurations(true);
    }
    async sendTestEvent(webhook) {
        if (!this.authManager.orgId)
            return false;
        try {
            await this.provisioningService.testWebhookDelivery(this.authManager.orgId, webhook.id);
            this.bannerUIManager.displayBanner({ text: `Test event sent for "${webhook.name}"` });
            return true;
        }
        catch (error) {
            this.bannerUIManager.displayBanner({ text: `Error sending test event for "${webhook.name}"`, type: "ERROR" });
            return false;
        }
    }
    async pauseOrResume(webhook) {
        if (!this.authManager.orgId)
            return;
        await this.provisioningService.updateWebhookConfiguration(this.authManager.orgId, webhook.id, {
            status: webhook.status === WebhookConfigurationStatus.ACTIVE
                ? WebhookConfigurationStatus.PAUSED
                : WebhookConfigurationStatus.ACTIVE,
        });
        void this.loadWebhookConfigurations();
    }
    async deleteWebhook(webhook) {
        if (!this.authManager.orgId)
            return;
        const confirmed = await this.deleteConfirmationDialogModel.showAndWait();
        if (!confirmed)
            return;
        await this.provisioningService.deleteWebhookConfiguration(this.authManager.orgId, webhook.id);
        void this.loadWebhookConfigurations();
        this.bannerUIManager.displayBanner({ text: `Webhook "${webhook.name}" successfully deleted` });
    }
}
__decorate([
    observable
], AdminWebhookListViewModel.prototype, "paginationInput", void 0);
__decorate([
    observable
], AdminWebhookListViewModel.prototype, "total", void 0);
__decorate([
    observable
], AdminWebhookListViewModel.prototype, "loadedWebhookConfigurations", void 0);
__decorate([
    observable
], AdminWebhookListViewModel.prototype, "addWebhookDialogOpen", void 0);
__decorate([
    observable
], AdminWebhookListViewModel.prototype, "secretRevealState", void 0);
__decorate([
    computed
], AdminWebhookListViewModel.prototype, "webhooks", null);
